import React, { useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import ReCAPTCHA from "react-google-recaptcha"
import { APP_CONFIG } from "src/app_config"
import { Button, Checkbox, Container, Image, Typography } from "src/UILibrary"
import Grid from "@mui/material/Grid"
import { ShowHeader } from "../../states/showHeader"
import { ShowToast } from "../../states/showToast"
import { useRecoilState } from "recoil"
import { Box } from "@mui/system"
import * as yup from "yup"
import UserSignupBack from "src/assets/imgs/user_signup_back.png"
import InputTextField from "src/components/atom/InputTextField"
import GoogleIcon from "src/assets/icons/google.svg"
import LineIcon from "src/assets/icons/line.png"
import { validateField } from "src/modules/validation"
import { useLocation, useNavigate } from "react-router-dom"
import { AuthenticationService } from "src/openapi"
import {
  MEMBER_TYPE,
  RESPONSE_CODE,
  SNACKBAR_TYPES,
  STORAGE_KEY,
  USER_ATTRIBITE_KEY,
} from "src/modules/const"
import { useLoginContext } from "src/hooks/useLoginContext"
import { getUserAttribute } from "src/modules/helper"
import { useMediaQuery } from "react-responsive"
import { ShowProgress } from "src/states/showProgress"
import { Amplify, Auth } from "aws-amplify"
import { AWS_CONFIG } from "src/aws_config"
import { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth"

Amplify.configure(AWS_CONFIG)

export const Signin = () => {
  const { t } = useTranslation("translation", { keyPrefix: "signin" })

  const isPC = useMediaQuery({ query: "(min-width: 1020px)" })

  const navigate = useNavigate()

  const { state } = useLocation()
  const { message } = state || ""

  const [, setShowHeader] = useRecoilState(ShowHeader)
  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { handleLogin, refreshMasterData, getLineAccessToken } = useLoginContext()

  const loginInfo = localStorage.getItem(STORAGE_KEY.LOGIN)

  const [verified, setVerified] = useState<boolean>(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [email, setEmail] = useState<string>(
    state ? state.email : loginInfo ? JSON.parse(loginInfo).email : ""
  )
  const [password, setPassword] = useState<string>(
    state || !loginInfo ? "" : JSON.parse(loginInfo).password
  )

  const [emailValidationMessage, setEmailValidationMessage] = useState<string>("")
  const [passwordValidationMessage, setPasswordValidationMessage] = useState<string>("")

  const [remeberInfo, setRememberInfo] = useState<boolean>(loginInfo ? true : false)

  useEffect(() => {
    setShowHeader(false)
  })

  useEffect(() => {
    if (message) {
      setShowToast({ show: true, text: message, type: SNACKBAR_TYPES.SUCCESS })
    }
  }, [setShowHeader, setShowToast, message])

  useEffect(() => {
    if (location.href.includes("access_token")) {
      setShowProgress(true)

      const accessTokenStart = location.href.indexOf("access_token") + 13
      const accessTokenEnd = location.href.indexOf("&id_token")
      const accessToken = location.href.substring(accessTokenStart, accessTokenEnd)

      handleAccessToken(accessToken)
    }
  }, [])

  useEffect(() => {
    if (location.href.includes("?code=")) {
      setShowProgress(true)
      const codeStart = location.href.indexOf("?code=") + 6
      const codeEnd = location.href.indexOf("&state")
      const code = location.href.substring(codeStart, codeEnd)

      getLineAccessToken(code)
        .then((token: any) => {
          handleAccessToken(token)
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        })
    }
  }, [])

  const handleAccessToken = (accessToken: string) => {
    const userType = localStorage.getItem(STORAGE_KEY.TEMP_USER_TYPE)
      ? parseInt(localStorage.getItem(STORAGE_KEY.TEMP_USER_TYPE) as string)
      : MEMBER_TYPE.USER

    refreshMasterData()

    AuthenticationService.getuser({
      accessToken: accessToken,
      userType: userType,
    })
      .then((res) => {
        localStorage.removeItem(STORAGE_KEY.TEMP_USER_TYPE)

        setShowProgress(false)
        if (res.status === RESPONSE_CODE.SUCCESS) {
          const attributes = res.data?.UserData?.UserAttributes as any[]
          const userType = getUserType(attributes)
          const userID = res.data?.UserData?.Username

          handleLogin(accessToken, userType, userID)

          navigate("/")
        } else if (res.status === RESPONSE_CODE.NOT_FOUND) {
          const attributes = res.data?.UserData?.UserAttributes as any[]
          const userType = getUserType(attributes)
          const userID = res.data?.UserData?.Username
          const email = getUserEmail(attributes)

          switch (userType) {
            case MEMBER_TYPE.USER:
              navigate("/signup/user/detail", {
                state: { userId: userID, email: email },
              })
              break
            case MEMBER_TYPE.ADVISER:
              navigate("/signup/adviser/detail", {
                state: { userId: userID, email: email },
              })
              break
            case MEMBER_TYPE.COMPANY:
              navigate("/signup/company/detail", {
                state: { userId: userID, email: email },
              })
              break
            case MEMBER_TYPE.ADMINISTRATOR:
              navigate("/signup/admin/detail", {
                state: { userId: userID, email: email },
              })
              break
          }
        } else if (res.status === RESPONSE_CODE.NOT_APPROVED) {
          setShowToast({
            show: true,
            text: t("please_wait_until_approve"),
            type: SNACKBAR_TYPES.INFORMATION,
          })
        } else {
          setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
          navigate("/signin")
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
        navigate("/signin")
      })
  }

  const validate = async () => {
    let ret = verified

    if (!verified) {
      setShowToast({ show: true, text: t("please_verify_recaptcha"), type: SNACKBAR_TYPES.WARNING })
    }

    try {
      await validateField(
        yup.object().shape({
          email: yup
            .string()
            .required(t("please_input_email"))
            .email(t("please_input_correct_email")),
        }),
        "email",
        email
      )
      ret = ret && true
      setEmailValidationMessage("")
    } catch (err: any) {
      setEmailValidationMessage(err.message)
      ret = ret && false
    }

    try {
      await validateField(
        yup.object().shape({
          password: yup
            .string()
            .required(t("please_input_password"))
            .min(8, t("password_length_min"))
            .max(20, t("password_length_max"))
            .test("isValidPassword", t("password_regex"), (value) => {
              const hasLowerCase = /[a-z]/.test(value)
              const hasNumber = /[0-9]/.test(value)

              return hasLowerCase && hasNumber
            }),
        }),
        "password",
        password
      )
      ret = ret && true
      setPasswordValidationMessage("")
    } catch (err: any) {
      setPasswordValidationMessage(err.message)
      ret = ret && false
    }

    return ret
  }

  const handleSignin = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)

    await refreshMasterData()

    AuthenticationService.signIn({
      email: email,
      password: password,
    })
      .then((res) => {
        setShowProgress(false)
        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowProgress(false)
          if (remeberInfo) {
            localStorage.setItem(
              STORAGE_KEY.LOGIN,
              JSON.stringify({ email: email, password: password })
            )
          } else {
            localStorage.removeItem(STORAGE_KEY.LOGIN)
          }

          const attributes = res.data?.UserData?.UserAttributes as any[]
          const userType = getUserType(attributes)
          const userID = res.data?.UserData?.Username

          handleLogin(res.data?.TokenData?.AuthenticationResult?.AccessToken, userType, userID)

          setShowToast({ show: true, text: t("success_login"), type: SNACKBAR_TYPES.SUCCESS })

          navigate("/")
        } else if (res.status === RESPONSE_CODE.NOT_FOUND) {
          const attributes = res.data?.UserData?.UserAttributes as any[]
          const userType = getUserType(attributes)
          const userID = getUserSub(attributes)

          switch (userType) {
            case MEMBER_TYPE.USER:
              navigate("/signup/user/detail", {
                state: { userId: userID, email: email },
              })
              break
            case MEMBER_TYPE.ADVISER:
              navigate("/signup/adviser/detail", {
                state: { userId: userID, email: email },
              })
              break
            case MEMBER_TYPE.COMPANY:
              navigate("/signup/company/detail", {
                state: { userId: userID, email: email },
              })
              break
            case MEMBER_TYPE.ADMINISTRATOR:
              navigate("/signup/admin/detail", {
                state: { userId: userID, email: email },
              })
              break
          }
        } else if (res.status === RESPONSE_CODE.FORBIDDEN) {
          AuthenticationService.resend({ email: email })

          navigate("/signup/verification", {
            state: { requireSignIn: true, email: email, password: password },
          })
        } else if (res.status === RESPONSE_CODE.UNAUTHORIZED) {
          setShowToast({
            show: true,
            text: t("please_input_correct_email_password"),
            type: SNACKBAR_TYPES.WARNING,
          })
        } else if (res.status === RESPONSE_CODE.NOT_APPROVED) {
          setShowToast({
            show: true,
            text: t("please_wait_until_approve"),
            type: SNACKBAR_TYPES.INFORMATION,
          })
        } else {
          setShowToast({ show: true, text: res.message as string, type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const getUserType = (userDatas: any[]) => {
    return parseInt(getUserAttribute(userDatas, USER_ATTRIBITE_KEY.USER_TYPE))
  }

  const getUserSub = (userDatas: any[]) => {
    return getUserAttribute(userDatas, USER_ATTRIBITE_KEY.AWS_ID)
  }

  const getUserEmail = (userDatas: any[]) => {
    return getUserAttribute(userDatas, USER_ATTRIBITE_KEY.EMAIL)
  }

  const handleGoogleSignin = () => {
    Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google })
  }

  const handleLineLogin = () => {
    Auth.federatedSignIn({ customProvider: "LINE-Login" })
  }

  return (
    <Grid id="whole-app" style={{ position: "relative", height: "100vh" }}>
      {isPC ? (
        <Grid
          id="main"
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "inherit" }}
        >
          <Container sx={{ width: "lg", display: "flex" }}>
            <Box sx={{ width: "50%", position: "relative" }}>
              <Image src={UserSignupBack} sx={{ width: "100%", zIndex: 1 }} />
              <Typography.SignupLogo
                sx={{
                  position: "absolute",
                  zIndex: 2,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                TASU
              </Typography.SignupLogo>
            </Box>
            <Box sx={{ width: "50%", height: "auto", display: "flex" }}>
              <Box sx={{ display: "flex", mx: "auto", my: "auto", flexDirection: "column" }}>
                <InputTextField
                  name={t("email")}
                  required
                  placeholder={t("email_placeholder")}
                  text={email}
                  handleText={setEmail}
                  validationMessage={emailValidationMessage}
                  maxLength={50}
                />
                <InputTextField
                  name={t("password")}
                  required
                  placeholder={t("password_placeholder")}
                  sx={{ mt: "20px" }}
                  password
                  text={password}
                  handleText={setPassword}
                  validationMessage={passwordValidationMessage}
                />
                <ReCAPTCHA
                  style={{ display: "block", marginTop: "20px" }}
                  sitekey={APP_CONFIG.GOOGLE_RECAPTCHA_SITE_KEY}
                  hl="ja"
                  ref={recaptchaRef}
                  onChange={() => setVerified(true)}
                  onExpired={() => setVerified(false)}
                />
                <Checkbox
                  label={t("remember_login_info")}
                  checked={remeberInfo}
                  onChange={(event, checked) => setRememberInfo(checked)}
                />
                <Button
                  variant="outlined"
                  sx={{
                    mt: "40px",
                    width: "240px",
                    borderRadius: 0,
                    borderColor: "text.primary",
                    py: "16px",
                  }}
                  onClick={() => handleSignin()}
                >
                  <Typography.ButtonText
                    sx={{ fontSize: "16px", fontFamily: "Noto Sans JP !important" }}
                  >
                    {t("signin")}
                  </Typography.ButtonText>
                </Button>

                <Box
                  sx={{
                    mt: "20px",
                    width: "240px",
                    p: 0,
                    cursor: "pointer",
                    display: "flex",
                  }}
                  onClick={() => navigate("/signup/type")}
                >
                  <Typography.ExtraSmallText sx={{ mr: "10px" }}>
                    {t("here_to_signup")}
                  </Typography.ExtraSmallText>
                  <Typography.ExtraSmallText>{">"}</Typography.ExtraSmallText>
                </Box>
                <Box
                  sx={{
                    mt: "20px",
                    width: "240px",
                    p: 0,
                    cursor: "pointer",
                    display: "flex",
                  }}
                  onClick={() => navigate("/password_reset/email")}
                >
                  <Typography.ExtraSmallText sx={{ mr: "10px" }}>
                    {t("here_to_password_reset")}
                  </Typography.ExtraSmallText>
                  <Typography.ExtraSmallText>{">"}</Typography.ExtraSmallText>
                </Box>
                <Typography.SubHeadNormal sx={{ mt: "80px" }}>
                  {t("signup_with_other_service")}
                </Typography.SubHeadNormal>
                <Box
                  sx={{
                    mt: "20px",
                    display: "flex",
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "white",
                      width: "230px",
                      mr: "20px",
                      my: "auto",
                      p: "10px",
                      ":hover": {
                        bgcolor: "#2DA389",
                      },
                    }}
                    onClick={() => handleGoogleSignin()}
                  >
                    <Image src={GoogleIcon} sx={{ height: "25px" }} />
                    <Typography.ButtonText
                      sx={{
                        fontFamily: "Noto Sans JP !important",
                        ml: "20px",
                        my: "auto",
                        fontWeight: "500",
                      }}
                    >
                      {t("google_signup")}
                    </Typography.ButtonText>
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#06C755",
                      width: "230px",
                      my: "auto",
                      p: "10px",
                      ":hover": {
                        bgcolor: "#2DA389",
                      },
                    }}
                    onClick={() => handleLineLogin()}
                  >
                    <Image src={LineIcon} sx={{ width: "25px", height: "25px" }} />
                    <Typography.ButtonText
                      sx={{
                        fontFamily: "Noto Sans JP !important",
                        ml: "20px",
                        my: "auto",
                        fontWeight: "500",
                        color: "white",
                      }}
                    >
                      {t("line_signup")}
                    </Typography.ButtonText>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Container>
        </Grid>
      ) : (
        <Container
          sx={{
            width: "100%",
            display: "block",
            p: "0px !important",
            backgroundImage: `url(${UserSignupBack})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <Box width="100%" height="50px"></Box>
          <Typography.SignupLogo sx={{ textAlign: "center" }}>TASU</Typography.SignupLogo>
          <Box
            sx={{
              display: "block",
              width: "calc(100% - 20px)",
              mx: "10px",
              mt: "50px",
              bgcolor: "white",
              px: "10px",
              py: "20px",
            }}
          >
            <Box sx={{ display: "flex", mx: "auto", my: "auto", flexDirection: "column" }}>
              <InputTextField
                name={t("email")}
                required
                placeholder={t("email_placeholder")}
                text={email}
                handleText={setEmail}
                validationMessage={emailValidationMessage}
                maxLength={50}
              />
              <InputTextField
                name={t("password")}
                required
                placeholder={t("password_placeholder")}
                sx={{ mt: "20px" }}
                password
                text={password}
                handleText={setPassword}
                validationMessage={passwordValidationMessage}
              />
              <ReCAPTCHA
                style={{ display: "block", marginTop: "20px", maxWidth: "calc(100% - 20px)" }}
                sitekey={APP_CONFIG.GOOGLE_RECAPTCHA_SITE_KEY}
                hl="ja"
                ref={recaptchaRef}
                onChange={() => setVerified(true)}
                onExpired={() => setVerified(false)}
              />
              <Checkbox
                label={t("remember_login_info")}
                checked={remeberInfo}
                onChange={(event, checked) => setRememberInfo(checked)}
              />
              <Button
                variant="outlined"
                sx={{
                  mt: "40px",
                  width: "240px",
                  borderRadius: 0,
                  borderColor: "text.primary",
                  py: "16px",
                }}
                onClick={() => handleSignin()}
              >
                <Typography.ButtonText
                  sx={{ fontSize: "16px", fontFamily: "Noto Sans JP !important" }}
                >
                  {t("signin")}
                </Typography.ButtonText>
              </Button>

              <Box
                sx={{
                  mt: "20px",
                  width: "240px",
                  p: 0,
                  cursor: "pointer",
                  display: "flex",
                }}
                onClick={() => navigate("/signup/type")}
              >
                <Typography.ExtraSmallText sx={{ mr: "10px" }}>
                  {t("here_to_signup")}
                </Typography.ExtraSmallText>
                <Typography.ExtraSmallText>{">"}</Typography.ExtraSmallText>
              </Box>
              <Box
                sx={{
                  mt: "20px",
                  width: "240px",
                  p: 0,
                  cursor: "pointer",
                  display: "flex",
                }}
                onClick={() => navigate("/password_reset/email")}
              >
                <Typography.ExtraSmallText sx={{ mr: "10px" }}>
                  {t("here_to_password_reset")}
                </Typography.ExtraSmallText>
                <Typography.ExtraSmallText>{">"}</Typography.ExtraSmallText>
              </Box>
              <Typography.SubHeadNormal sx={{ mt: "80px", fontSize: "18px" }}>
                {t("signup_with_other_service")}
              </Typography.SubHeadNormal>
              <Box
                sx={{
                  mt: "20px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "white",
                    width: "230px",
                    mr: "20px",
                    my: "auto",
                    p: "10px",
                    ":hover": {
                      bgcolor: "#2DA389",
                    },
                  }}
                  onClick={() => handleGoogleSignin()}
                >
                  <Image src={GoogleIcon} sx={{ height: "25px" }} />
                  <Typography.ButtonText
                    sx={{
                      fontFamily: "Noto Sans JP !important",
                      ml: "20px",
                      my: "auto",
                      fontWeight: "500",
                    }}
                  >
                    {t("google_signup")}
                  </Typography.ButtonText>
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#06C755",
                    width: "230px",
                    my: "auto",
                    p: "10px",
                    mt: "20px",
                    ":hover": {
                      bgcolor: "#2DA389",
                    },
                  }}
                  onClick={() => handleLineLogin()}
                >
                  <Image src={LineIcon} sx={{ width: "25px", height: "25px" }} />
                  <Typography.ButtonText
                    sx={{
                      fontFamily: "Noto Sans JP !important",
                      ml: "20px",
                      my: "auto",

                      fontWeight: "500",
                      color: "white",
                    }}
                  >
                    {t("line_signup")}
                  </Typography.ButtonText>
                </Button>
              </Box>
            </Box>
          </Box>
          <Box width="100%" height="50px"></Box>
        </Container>
      )}
    </Grid>
  )
}
